import React from 'react';
import { AppState, Platform } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { HomeAPI } from '@/services/residents/api/HomeAPI';
import { setUnreadBulletinsCount } from '@/store/slices/residents/homeSlice';

export const useUnreadBulletinsCount = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.auth);
  const propertyId = user?.propertyId;
  const isFocused = useIsFocused();
  const count = useAppSelector((s) => s.home.unreadBulletinsCount || 0);

  // Guards and state
  const isFetchingRef = React.useRef(false);
  const errorBackoffUntilRef = React.useRef<number>(0);
  const appStateRef = React.useRef<string>(AppState.currentState);

  // Track app visibility/activity (native)
  React.useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      appStateRef.current = state;
    });
    return () => {
      sub.remove();
    };
  }, []);

  const isEnvironmentActive = React.useCallback(() => {
    if (Platform.OS === 'web') {
      try {
        // Only poll when tab is visible and focused
        const doc: any = typeof globalThis !== 'undefined' ? (globalThis as any).document : null;
        const isVisible = doc ? doc.visibilityState === 'visible' : true;
        const isDocFocused = doc && typeof doc.hasFocus === 'function' ? doc.hasFocus() : true;
        return isVisible && isDocFocused;
      } catch {
        return true;
      }
    }
    // Native: only when app is active
    return appStateRef.current === 'active';
  }, []);

  const loadUnread = React.useCallback(async () => {
    // Skip if no property or env not active
    if (propertyId === undefined || propertyId === null) {
      dispatch(setUnreadBulletinsCount(0));
      return;
    }
    if (!isEnvironmentActive()) return;

    // Backoff after errors
    const now = Date.now();
    if (now < errorBackoffUntilRef.current) return;

    // In-flight guard
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const c = await HomeAPI.getUnreadBulletinsCount(propertyId);
      dispatch(setUnreadBulletinsCount(c || 0));
      // reset backoff on success
      errorBackoffUntilRef.current = 0;
    } catch {
      // minimal backoff (60s) on error
      errorBackoffUntilRef.current = Date.now() + 60_000;
      dispatch(setUnreadBulletinsCount(0));
    } finally {
      isFetchingRef.current = false;
    }
  }, [propertyId, dispatch, isEnvironmentActive]);

  React.useEffect(() => {
    loadUnread();
  }, [loadUnread]);

  React.useEffect(() => {
    if (isFocused) {
      loadUnread();
    }
  }, [isFocused, loadUnread]);

  React.useEffect(() => {
    const interval = setInterval(loadUnread, 60_000); // 60 seconds for more responsive notifications
    return () => clearInterval(interval);
  }, [loadUnread]);

  return count;
};


